import { useInfiniteQuery } from "@tanstack/react-query";
import { History, Search, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage, unwrap } from "../api/client";
import { useOrgId } from "../api/org";
import { Alert, Badge, Button, Card, Empty, PageHead, Spinner } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { auditDetails, auditLabel } from "../lib/auditLabels";
import { useDates } from "../lib/useDates";
import { useDebounced } from "./admin/Orgs";
import { roleName } from "./Team";

export function AuditTable({ orgId, showCompany }: { orgId?: string; showCompany?: boolean }) {
  const t = useT();
  const dates = useDates();
  const [q, setQ] = useState("");
  const [actor, setActor] = useState("");
  const action = useDebounced(q.trim());
  const who = useDebounced(actor.trim());
  const log = useInfiniteQuery({
    queryKey: ["audit", orgId ?? "all", action, who],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const query = { limit: 50, cursor: pageParam, action: action || undefined, actor: who || undefined };
      return orgId
        ? unwrap(api.GET("/v1/orgs/{org_id}/audit-log", { params: { path: { org_id: orgId }, query } }))
        : unwrap(api.GET("/v1/admin/audit-log", { params: { query } }));
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const items = log.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card pad={false}>
      <div className="toolbar">
        <div className="input-icon">
          <Search />
          <input className="input" placeholder={t("team.audit.filter.action")} aria-label={t("team.audit.filter.actionLabel")} dir="auto" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="input-icon">
          <Search />
          <input className="input" placeholder={t("team.audit.filter.who")} aria-label={t("team.audit.filter.who")} dir="auto" value={actor} onChange={(e) => setActor(e.target.value)} />
        </div>
      </div>
      {log.isLoading ? (
        <Spinner label={t("team.common.loading")} />
      ) : log.error ? (
        <div className="card-body">
          <Alert tone="danger">{errorMessage(log.error)}</Alert>
        </div>
      ) : !items.length ? (
        <Empty icon={<History />} title={t("team.audit.empty")} />
      ) : (
        <div className="table-wrap">
          <table className="table audit-table">
            <thead>
              <tr>
                <th>{t("team.audit.col.when")}</th>
                <th>{t("team.audit.col.who")}</th>
                {showCompany && <th>{t("team.audit.col.company")}</th>}
                <th>{t("team.audit.col.what")}</th>
                <th className="hide-sm">{t("team.audit.col.details")}</th>
                <th className="hide-sm">{t("team.audit.col.ip")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id}>
                  <td className="small" style={{ minWidth: 96 }}>{dates.dateTime(e.created_at)}</td>
                  <td>
                    <div className="row" style={{ gap: 6, flexWrap: "nowrap" }}>
                      <span className="cell-main ellipsis" dir="auto" style={{ maxWidth: 190 }} title={e.actor_email ?? undefined}>
                        {e.actor_email ?? e.actor_name ?? (e.action.startsWith("identity.") || e.action.startsWith("sso.") ? t("team.audit.actor.sso") : t("team.audit.actor.system"))}
                      </span>
                      {e.actor_platform_admin && (
                        <Badge plain tone="warn">
                          <ShieldCheck size={11} /> {t("team.audit.staff")}
                        </Badge>
                      )}
                    </div>
                  </td>
                  {showCompany && (
                    <td className="small nowrap">
                      {e.organization_id ? <Link to={`/admin/orgs/${e.organization_id}`} dir="auto">{e.organization_name ?? e.organization_id.slice(0, 8)}</Link> : "—"}
                    </td>
                  )}
                  <td className="what">
                    <div className="cell-main">{auditLabel(t, e.action)}</div>
                    <div className="cell-sub mono" dir="ltr">{e.action}</div>
                  </td>
                  <td className="hide-sm">
                    <div className="audit-data" dir="auto" title={JSON.stringify(e.data)}>{auditDetails(t, e, (r) => roleName(t, r)) || "—"}</div>
                  </td>
                  <td className="hide-sm mono small muted" dir="ltr">{e.ip ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {log.hasNextPage && (
        <div className="card-foot">
          <Button size="sm" loading={log.isFetchingNextPage} onClick={() => log.fetchNextPage()}>
            {t("team.audit.loadOlder")}
          </Button>
        </div>
      )}
    </Card>
  );
}

export function AuditLog() {
  const t = useT();
  const orgId = useOrgId();
  return (
    <>
      <PageHead title={t("team.audit.page.title")} description={t("team.audit.page.description")} />
      <AuditTable orgId={orgId} />
    </>
  );
}

export function AdminAuditLog() {
  const t = useT();
  return (
    <>
      <PageHead title={t("team.audit.page.title")} description={t("team.audit.page.adminDescription")} />
      <AuditTable showCompany />
    </>
  );
}
